import {
  Store as VuexStore,
  ActionContext,
  ActionTree,
  CommitOptions,
  DispatchOptions,
  GetterTree,
  Module,
  MutationTree,
} from "vuex";
import { RootState } from "@/store/root-state";
import { SlyShoppingState } from "./sly-shopping-state";
import { SlyShoppingPrizeDescription } from "@/models/sly-shopping-prize-description";
import { SlyShoppingWave } from "@/models/sly-shopping-wave";
import { SlyShoppingStatusDescription } from "@/models/sly-shopping-status-description";
import { SlyShoppingPrizeView } from "@/models/sly-shopping-prize-view";
import slyShoppingService from "@/services/sly-shopping-service";
import { SlyShoppingPrizeResponse } from "@/models/sly-shopping-prize-response";
import { SlyShoppingPrizeItemView } from "@/models/sly-shopping-prize-item-view";
import CouponService from "@/services/coupon-service";
import { Coupon } from "@/models/coupon-check";
import mixins from "@/mixins";
import dayjs from "dayjs";
import "dayjs/locale/ru";

// initial state
const state: SlyShoppingState = {
  prizeDescriptions: [] as SlyShoppingPrizeDescription[],
  waves: [] as SlyShoppingWave[],
  statuseDescriptions: [] as SlyShoppingStatusDescription[],
  shoppingLoading: false,
};

export type Getters = {
  slyShoppingView: (
    state: SlyShoppingState
  ) => (rizeResponses: SlyShoppingPrizeResponse[]) => SlyShoppingPrizeView[];
  slyShoppingGetPrizes: (
    state: SlyShoppingState
  ) => (prizes?: string, waveId?: string, compaingStart?: Date) => SlyShoppingPrizeItemView[];
  shoppingLoading(state: SlyShoppingState): boolean;
};

const getters: GetterTree<SlyShoppingState, RootState> = {
  slyShoppingView:
    (state: SlyShoppingState) =>
    (prizeResponses: SlyShoppingPrizeResponse[]): SlyShoppingPrizeView[] => {
      const result: SlyShoppingPrizeView[] = [];
      prizeResponses.forEach((prizeResponse) => {
        const newPrizeView = new SlyShoppingPrizeView();
        newPrizeView.email = prizeResponse.email;
        newPrizeView.prize_sent = prizeResponse.prize_sent
          ? new Date(prizeResponse.prize_sent.toString().split("T")[0])
          : undefined;
        newPrizeView.id = prizeResponse.id;
        newPrizeView.validity = prizeResponse.validity;
        newPrizeView.prize = prizeResponse.prize;
        newPrizeView.auth_id = prizeResponse.auth_id?.toString();
        newPrizeView.phone = prizeResponse.phone;
        newPrizeView.wave_id = prizeResponse.wave_id?.toString();
        newPrizeView.start = prizeResponse.start;
        if (prizeResponse.prize_status_id) {
          newPrizeView.prizeStatus = state.statuseDescriptions.find(
            (status) => status.prize_status_id === prizeResponse.prize_status_id
          )?.prize_status_desc;
        }
        /*const prizeIds = newPrizeView.prize?.split(",");
        if (prizeIds) {
          const prizeDescription = state.prizeDescriptions.find(
            (prizeDescription) => prizeDescription.prize_id?.toString() === prizeIds[0]
          );
          if (prizeDescription) {
            const firstItem = getPrizeItem(prizeDescription, newPrizeView.start);
            if (firstItem) {
              newPrizeView.validUntil = firstItem.validUntil;
            }
          }
        }*/

        result.push(newPrizeView);
      });
      return result;
    },
  slyShoppingGetPrizes:
    (state: SlyShoppingState) =>
    (prizes: string, waveId: string, compaingStart: Date): SlyShoppingPrizeItemView[] => {
      const result: SlyShoppingPrizeItemView[] = [];
      const prizeIds = prizes.split(",");
      if (prizeIds) {
        prizeIds.forEach((prizeId) => {
          const prizeDescription = state.prizeDescriptions.find(
            (prizeDescription) =>
              prizeDescription.prize_id?.toString() === prizeId &&
              prizeDescription.wave_id?.toString() === waveId
          );
          if (prizeDescription) {
            const newItem = getPrizeItem(prizeDescription, compaingStart);
            if (newItem) {
              result.push(newItem);
            }
          }
        });
      }
      return result;
    },

  shoppingLoading(state: SlyShoppingState): boolean {
    return state.shoppingLoading;
  },
};

function getPrizeItem(
  prizeDescription: SlyShoppingPrizeDescription,
  compaingStart?: Date
): SlyShoppingPrizeItemView | null {
  const newItem: SlyShoppingPrizeItemView = prizeDescription;
  const couponSearch = new Coupon();
  couponSearch.campaignStart = compaingStart;
  couponSearch.gcn = prizeDescription.gcn?.toString();
  CouponService.getCouponDetailed(couponSearch)
    .then((details) => {
      newItem.descriptionList = details.descriptionList;
      newItem.validFrom = new Date(details.validFrom);
      newItem.validUntil = new Date(details.validUntil);
      return newItem;
    })
    .catch(() => {
      return newItem;
    });
  return newItem;
}

// actions
export interface Actions {
  loadDictionaryes({ commit }: ActionContext<SlyShoppingState, RootState>): void;
}

const actions: ActionTree<SlyShoppingState, RootState> & Actions = {
  loadDictionaryes({ commit }: ActionContext<SlyShoppingState, RootState>) {
    commit("setShoppingLoading", true);
    slyShoppingService.getPrizeDescriptions().then(
      (prizeDescriptions) => {
        commit("setPrizeDescriptions", prizeDescriptions);
        commit("setShoppingLoading", false);
      },
      (error) => {
        commit("setShoppingLoading", false);
      }
    );
    slyShoppingService.getStatusDescriptions().then(
      (statusDescriptions) => {
        commit("setStatusDescriptions", statusDescriptions);
        commit("setShoppingLoading", false);
      },
      (error) => {
        commit("setShoppingLoading", false);
      }
    );
    slyShoppingService.getWaves().then(
      (waves) => {
        commit("setWaves", waves);
        commit("setShoppingLoading", false);
      },
      (error) => {
        commit("setShoppingLoading", false);
      }
    );
  },
};

// mutations
export type Mutations = {
  setShoppingLoading(state: SlyShoppingState, payload: boolean): void;
  setWaves(state: SlyShoppingState, payload: SlyShoppingWave[]): void;
  setPrizeDescriptions(state: SlyShoppingState, payload: SlyShoppingPrizeDescription[]): void;
  setStatusDescriptions(state: SlyShoppingState, payload: SlyShoppingStatusDescription[]): void;
};

const mutations: MutationTree<SlyShoppingState> & Mutations = {
  setShoppingLoading(state: SlyShoppingState, payload: boolean): void {
    state.shoppingLoading = payload;
  },
  setWaves(state: SlyShoppingState, payload: SlyShoppingWave[]): void {
    state.waves = payload;
  },
  setPrizeDescriptions(state: SlyShoppingState, payload: SlyShoppingPrizeDescription[]): void {
    state.prizeDescriptions = payload;
  },
  setStatusDescriptions(state: SlyShoppingState, payload: SlyShoppingStatusDescription[]): void {
    state.statuseDescriptions = payload;
  },
};

// store
export type SlyShoppingStore<S = SlyShoppingState> = Omit<VuexStore<S>, "getters" | "commit" | "dispatch"> & {
  commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
    key: K,
    payload: P,
    options?: CommitOptions
  ): ReturnType<Mutations[K]>;
} & {
  // TODO: How to configure a module without any actions?
  dispatch<K extends keyof Actions>(
    key: K,
    payload?: Parameters<Actions[K]>[1],
    options?: DispatchOptions
  ): ReturnType<Actions[K]>;
} & {
  getters: {
    [K in keyof Getters]: ReturnType<Getters[K]>;
  };
};

export const slyShopping: Module<SlyShoppingState, RootState> = {
  state,
  getters,
  actions,
  mutations,
};
